import type { FeedbackDto, IssueStatus, ReportsDto } from '@nsb/core';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { api, errorText, queryClient, useApi } from '../api.ts';
import { useFeedbackRecord } from '../components/feedback-context.ts';
import { Button, Confirm, ErrorLine, LoadError, Loading, Page, Tag, Title, Toggle } from '../components/ui.tsx';
import { dateWords } from '../days.ts';
import { currentEnvironment, type Environment } from '../environment.ts';
import { hasPlannedSwitch, linkedReport, plannedStatus, reportDone, reportList, withReportIn, type ReportFilter } from './reportList.ts';

const STATUS_WORDS: Record<'bug' | 'feature', Record<IssueStatus, string>> = {
  bug: { received: 'received', planned: 'planned', fixed: 'fixed' },
  feature: { received: 'new', planned: 'planned', fixed: 'shipped' },
};

/**
 * When one is done, where it is marked done (Jason, 2026-10-07: "they should be marked fixed when the fix has been
 * implemented, tested, and verified ... a bug should be considered a little wholisticlaly, asking yourself 'is this
 * sytemic?'"): read after "Mark fixed" or "Mark shipped".
 */
const DONE_RULE: Record<'bug' | 'feature', string> = {
  bug: 'once the fix is built, tested and seen working, wherever the same fault was',
  feature: 'once it is built, tested and seen working',
};

/** Bugs and requests, read and written by this page and the Settings menu's count alike. */
const REPORTS_KEY = ['feedback', 'reports'] as const;

/**
 * Everything lands with Jason (spec §10): bugs, feature requests, comments.
 * A comment that is really a bug flips to an issue and back; a bug that is
 * really a wish becomes a feature request. The reporter only sees the reply.
 * In Settings → Development tools, with what is about the software itself
 * (Jason, 2026-10-04: "triage is a dumb menu"). It lists what is still open: a
 * bug marked fixed or a feature marked shipped leaves it, kept under Done
 * (L-10, Jason, 2026-10-07: "bugs marked as fixed should should not be in this list").
 * Every open report has a Planned switch, the work queue (labelled `planned` on
 * its GitHub issue, bug or feature alike); being done is marked apart from it.
 */
export function Triage() {
  const [filter, setFilter] = useState<ReportFilter>('all');
  const { data, error } = useApi<ReportsDto>(REPORTS_KEY, '/feedback/reports');
  useFeedbackRecord(null, null);
  // A link to one report (#<id>, from its GitHub issue or the old /triage) opens it, done or not: Done is on for a done
  // one until it is turned off, and the report is brought into view once it is there.
  const linked = linkedReport(useLocation().hash);
  const linkedDone = !!data?.reports.some((f) => f.id === linked && reportDone(f));
  const [doneChosen, chooseDone] = useState<{ link: string | null; on: boolean } | null>(null);
  const showDone = doneChosen && doneChosen.link === linked ? doneChosen.on : linkedDone;
  const broughtIntoView = useRef<string | null>(null);
  useEffect(() => {
    if (!linked || broughtIntoView.current === linked) return;
    const el = document.getElementById(linked);
    if (!el) return;
    broughtIntoView.current = linked;
    el.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [linked, data, showDone]);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const { shown, counts, done, doneUnlisted } = reportList(data.reports, filter, showDone, data.done);
  return (
    <Page>
      <Title kicker="Settings · development tools">Bugs and feature requests</Title>
      {/* Where these come from (Jason, 2026-10-07: "i can't tell if this is fake development data or real bugs, or where bugs i report where in development go"). */}
      <p className="-mt-3 mb-6 max-w-measure text-ink-2">{sourceWords(currentEnvironment())}</p>
      <div className="mb-6 flex flex-wrap items-center gap-x-5">
        {(['all', 'bug', 'feature', 'comment'] as const).map((k) => (
          <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)} className={`label min-h-11 ${filter === k ? '!text-ink underline underline-offset-4' : ''}`}>
            {k === 'all' ? 'All' : k === 'bug' ? 'Bugs' : k === 'feature' ? 'Feature requests' : 'Comments'} · {counts[k]}
          </button>
        ))}
        {/* The done ones, off by default: a bug fixed, a feature shipped. Shown, they are listed with the rest, reading as done. */}
        <span aria-hidden="true" className="hidden h-5 border-l border-rule sm:block" />
        <button type="button" aria-pressed={showDone} onClick={() => chooseDone({ link: linked, on: !showDone })} className={`label min-h-11 ${showDone ? '!text-ink underline underline-offset-4' : ''}`}>
          Done · {done}
        </button>
      </div>
      {shown.length === 0 ? <p className="text-ink-2">{showDone ? 'Nothing here.' : 'Nothing open here.'}</p> : null}
      {/* Every open report is listed, however old; of the done ones, the newest (the API's DONE_LISTED), and the page says so. */}
      {showDone && doneUnlisted > 0 ? (
        <p className="mb-4 text-sm text-ink-2">
          The newest <span className="num">{done - doneUnlisted}</span> done are listed; the <span className="num">{doneUnlisted}</span> done before them are kept and not listed here.
        </p>
      ) : null}
      <ul className="divide-y divide-rule border-y border-rule">
        {shown.map((f) => (
          <Item key={f.id} f={f} />
        ))}
      </ul>
    </Page>
  );
}

/**
 * Where the reports in the list were filed and where they go, in words: the deployed portal's become GitHub issues,
 * which Claude Code works from; a development build's own (Data: Local) stay in this machine's database alone.
 */
function sourceWords(env: Environment | null): string {
  if (env === 'local')
    return 'Filed on this machine’s development portal (Data: Local), by you or with test accounts. They stay in this machine’s database: they never become GitHub issues and the live portal never sees them. Switch Data to Production to see the live portal’s.';
  if (env === 'production')
    return 'Filed in the live portal, by you or a client. Each becomes a GitHub issue Claude Code works from: planned is labelled planned there, and marking one fixed or shipped, or deleting it, closes its issue.';
  return 'Filed in the portal, by you or a client. Each becomes a GitHub issue Claude Code works from: planned is labelled planned there, and marking one fixed or shipped, or deleting it, closes its issue.';
}

function Item({ f }: { f: FeedbackDto }) {
  const [reply, setReply] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const ruleId = useId();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['feedback'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const patch = (body: Record<string, unknown>) =>
    run(async () => {
      const report = await api<FeedbackDto>(`/feedback/${f.id}`, { method: 'PATCH', body });
      // Into the list as it is now, before it is read again: marked fixed or shipped it leaves at once, marked open again it comes back.
      queryClient.setQueryData<ReportsDto>(REPORTS_KEY, (list) => withReportIn(list, report));
    });
  const type = f.kind === 'comment' ? null : f.issueType;
  const done = reportDone(f);
  const [title, ...rest] = f.body.split('\n');
  return (
    // A wide screen reads each item as a register: who, when and where in a column of its own, then what they said.
    <li id={f.id} className="py-5 lg:grid lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] lg:gap-x-10">
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          {/* Its address, the way to name it (Jason, 2026-10-07: "lets get numbers on them so they can be addressed directly"): L-7 on this machine, P-12 on the live portal. */}
          <span className="label !text-ink">{f.address}</span>
          {type === 'bug' ? <Tag tone="alert">bug</Tag> : type === 'feature' ? <Tag tone="slate">feature</Tag> : <Tag tone="moss">comment</Tag>}
          {type ? <Tag tone={done ? 'moss' : 'plain'}>{STATUS_WORDS[type][f.issueStatus]}</Tag> : null}
          <span className="label">
            {f.reporterName} · {f.reporterRole}
            {f.viewedAs ? ` · seeing it as a ${f.viewedAs}` : ''} · {dateWords(f.createdAt, 'date')}
          </span>
        </div>
        <p className="mt-1 text-sm text-ink-3">
          {f.projectId ? (
            <Link to={`/p/${f.projectId}`} className="link">
              {f.projectName}
            </Link>
          ) : (
            'No project'
          )}
          {f.recordLabel ? ` · ${f.recordLabel}` : ''}
          {f.screen && f.screen !== 'seed' ? ` · ${f.screen}` : ''}
          {f.githubIssueUrl ? (
            <>
              {' · '}
              <a href={f.githubIssueUrl} className="link" target="_blank" rel="noreferrer">
                GitHub
              </a>
            </>
          ) : null}
          {f.hasScreenshot ? (
            <>
              {' · '}
              <a href={`/api/feedback/${f.id}/screenshot`} className="link" target="_blank" rel="noreferrer">
                screenshot
              </a>
            </>
          ) : null}
        </p>
      </div>
      <div className="mt-2 min-w-0 max-w-column lg:mt-0">
        <p className={`text-lg ${done ? 'text-ink-2' : ''}`}>{title}</p>
        {rest.join('\n').trim() ? <p className="mt-1 whitespace-pre-wrap text-sm text-ink-2">{rest.join('\n').trim()}</p> : null}
        {f.replies.map((r) => (
          <div key={r.id} className="mt-3 border-l border-rule pl-3 text-sm">
            <span className="label">{r.authorName}</span>
            <p className="whitespace-pre-wrap text-ink-2">{r.body}</p>
          </div>
        ))}
        {type ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            {/* Planned, the work queue: on, planned; off, received (a feature: new) again. A done one has none. */}
            {hasPlannedSwitch(f) ? (
              <Toggle
                checked={f.issueStatus === 'planned'}
                onChange={(on) => {
                  if (!busy) void patch({ issueStatus: plannedStatus(on) });
                }}
                label="Planned"
              />
            ) : null}
            {done ? (
              // Marked open again it comes back unplanned, its switch off.
              <button type="button" className="link min-h-11 text-ink-2" onClick={() => void patch({ issueStatus: 'received' })}>
                Open it again
              </button>
            ) : (
              <span className="py-2.5">
                <button type="button" className="link text-ink-2" aria-describedby={ruleId} onClick={() => void patch({ issueStatus: 'fixed' })}>
                  Mark {STATUS_WORDS[type].fixed}
                </button>{' '}
                <span id={ruleId} className="text-ink-3">
                  {DONE_RULE[type]}
                </span>
              </span>
            )}
          </div>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
          {type === 'bug' ? (
            <button type="button" className="link text-ink-2" onClick={() => void patch({ issueType: 'feature' })}>
              It is a feature request
            </button>
          ) : null}
          {type === 'feature' ? (
            <button type="button" className="link text-ink-2" onClick={() => void patch({ issueType: 'bug' })}>
              It is a bug
            </button>
          ) : null}
          {f.kind === 'comment' ? (
            <button type="button" className="link text-ink-2" onClick={() => void patch({ kind: 'issue', issueType: 'bug' })}>
              It is about the software — make it a bug
            </button>
          ) : null}
          {deleting ? null : (
            <button type="button" className="link text-ink-3 hover:text-alert" onClick={() => setDeleting(true)}>
              Delete
            </button>
          )}
        </div>
        {deleting ? (
          <Confirm
            prompt={`Delete this report for good?${f.githubIssueUrl ? ' Its GitHub issue is closed as not planned.' : ''}`}
            busy={busy}
            onYes={() => void run(() => api(`/feedback/${f.id}`, { method: 'DELETE' }))}
            onNo={() => setDeleting(false)}
          />
        ) : null}
        <div className="mt-3 flex items-end gap-3">
          <textarea rows={1} aria-label="Reply" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the reporter" className="field flex-1 resize-y" />
          <Button
            tone="quiet"
            busy={busy}
            disabled={!reply.trim()}
            onClick={() =>
              void run(async () => {
                await api(`/feedback/${f.id}/replies`, { body: { body: reply } });
                setReply('');
              })
            }
          >
            Reply
          </Button>
        </div>
        <ErrorLine error={err} />
      </div>
    </li>
  );
}
